import { Box, HoverCard, Stack, Text, Tooltip } from '@mantine/core';
import { useContext } from 'react';
import SingleCustomerContext from '../../Customer-Context/SingleCustomerContext';
import { DomainHeaderIcon, ProductAreaHeaderIcon } from '../../../../icons/x-symbol-svgrepo-com';
import { stripHtml } from '../../../../utils';

interface Idea {
  _id: string;
  title: string;
  status: string;
  componentId: string;
  [key: string]: any;
}

interface TitleCellProps {
  record: Idea;
}

export function TitleCell({ record }: TitleCellProps) {

  const { domains, allComponents, threeSixtyConfig } = useContext(SingleCustomerContext);

  const areaColors = [
    {
      name: 'Dashboard',
      color: '#B11254',  // Pink icon
      background: '#FFE4E1' // Light pink background
    },
    {
      name: 'Name of area',
      color: '#A21DAF',  // Purple icon
      background: '#F0E6FF' // Light purple background
    },
    {
      name: 'Name of other area',
      color: '#7A1CCC',  // Deep purple icon
      background: '#F5E6FA' // Light purple background
    },
    {
      name: 'Integrations',
      color: '#015C99',  // Blue icon
      background: '#E6F7FF' // Light blue background
    },
    {
      name: 'Payments',
      color: '#096860',  // Green icon
      background: '#E6FFE6' // Light green background
    },
    {
      name: 'Area',
      color: '#3A6C02',  // Lime green icon
      background: '#F0FFE6' // Light lime background
    },
    {
      name: 'Area 22',
      color: '#985602',  // Yellow icon
      background: '#FFFAE6' // Light yellow background
    },
    {
      name: 'CRM',
      color: '#BF3A0C',  // Orange-red icon
      background: '#FFE6E6' // Light red background
    }
  ];

  // Find the dropdown menu item that has displayIcon set to true
  const displayIconItem = threeSixtyConfig?.dropdownMenu?.find(
    (item: any) => item.displayIcon === true
  );

  // Use the groupBy value from that item
  const groupBy = displayIconItem?.groupBy || 'domainId';

  const relevantItems = groupBy === 'domainId' ? domains : allComponents;
  const relevantItem = relevantItems?.find(item => item._id === record[groupBy]);

  // If grouping by componentId or status, use the color mapping
  let color = '#1D47DB';
  let background = '#E2E8FE';

  if (groupBy === 'componentId' || groupBy === 'status') {
    const id = record[groupBy] || '';
    const charCodeSum = id
      .split('')
      .reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
    const colorIndex = Math.abs(charCodeSum) % areaColors.length;
    const colorMapping = areaColors[colorIndex];
    color = colorMapping.color;
    background = colorMapping.background;
  } else if (relevantItem) {
    color = relevantItem.color || '#1D47DB';
    background = relevantItem.backgroundColor || '#E2E8FE';
  }

  return (
    <Box 
      component="span" 
      sx={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        gap: '8px',
      }} 
    >
      <Box 
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '24px',
          borderRadius: '4px',
          backgroundColor: background
        }}
      >
        {
          groupBy === 'domainId' ?
            <Tooltip label={record?.componentObj?.domain} withinPortal>
              <Stack align='center' justify='center'>
                <DomainHeaderIcon color={color} fill={color} size={16} />
              </Stack>
            </Tooltip>
            :
            <Tooltip label={record?.componentObj?.name} withinPortal>
              <Stack align='center' justify='center'>
                <ProductAreaHeaderIcon color={color} fill={color} size={16} />
              </Stack>
            </Tooltip>
        }
      </Box>
      <HoverCard withinPortal /* shadow="xl" */>
        <HoverCard.Target>
          <Text 
            truncate
            w={310}
            ml={4}
            sx={{ textAlign: 'left' }}
          >
            {record.title}
          </Text>
        </HoverCard.Target>
        <HoverCard.Dropdown sx={{boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'}}>
          <Box p="xs" w={320}  >
            <Text fw={500} mb={4} lineClamp={1}>{record?.title}</Text>
            <Text
              size="sm"
              sx={{
                whiteSpace: 'pre-wrap',
                maxHeight: 100,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                textOverflow: 'ellipsis'
              }}
            >
              {stripHtml(record.description)}
            </Text>
          </Box>
        </HoverCard.Dropdown>
      </HoverCard>
      
      
    </Box>
  );
} 